import type { AgendyAisAisDto } from '@gov-nx/api';
import { useLocale } from '@gov-nx/core/service';
import type { PageCode } from '@gov-nx/module/page';
import { DataTable, InfoBanner } from '@gov-nx/ui/web';
import { constructValidUrl, toDateReadable } from '@gov-nx/utils/common';

interface AisViewProps {
	code: PageCode;
	aisData: AgendyAisAisDto;
}

export const AisView = ({ code, aisData }: AisViewProps) => {
	const { t: ts } = useLocale(code);

	if (!aisData || Object.keys(aisData).length <= 1)
		return (
			<InfoBanner
				headline={ts('stav.chyba.zatim-tu-nic-neni')}
				text={ts('stav.chyba.nenasli-jsme-zadne-udaje')}
			/>
		);

	const url = aisData?.spravce?.web
		? constructValidUrl(aisData.spravce.web)
		: null;

	return (
		<div className="pt-(--spacing-xs) pb-(--spacing-s-nudge) pl-(--spacing-s-nudge) pr-(--spacing-s-nudge) text-body-s">
			<DataTable responsive>
				<tbody>
					{aisData?.datumVytvoreni && (
						<tr>
							<td className="sm:w-1/2">{ts('data.datum-vytvoreni')}</td>
							<td className="sm:w-1/2 font-bold">
								{toDateReadable(aisData.datumVytvoreni)}
							</td>
						</tr>
					)}
					{aisData?.kategorie?.nazev && (
						<tr>
							<td>{ts('data.kategorie')}</td>
							<td className="font-bold">{aisData.kategorie.nazev}</td>
						</tr>
					)}
					{aisData?.verejny?.nazev && (
						<tr>
							<td>{ts('data.verejny')}</td>
							<td className="font-bold">{aisData.verejny.nazev}</td>
						</tr>
					)}
					{aisData?.charakteristika && (
						<tr>
							<td>{ts('data.charakteristika')}</td>
							<td className="font-bold">{aisData.charakteristika}</td>
						</tr>
					)}
					{aisData?.spravce?.nazev && (
						<tr>
							<td>{ts('data.spravce')}</td>
							<td className="font-bold">{aisData.spravce.nazev}</td>
						</tr>
					)}
					{aisData?.spravce?.ic && (
						<tr>
							<td>{ts('data.ico')}</td>
							<td className="font-bold">{aisData.spravce.ic}</td>
						</tr>
					)}
					{url && (
						<tr>
							<td>{ts('data.web')}</td>
							<td className="font-bold">
								<a
									href={url}
									target="_blank"
									rel="noreferrer noopener">
									{url}
								</a>
							</td>
						</tr>
					)}
					{aisData?.spravce?.email && (
						<tr>
							<td>{ts('data.email')}</td>
							<td className="font-bold">
								<a href={`mailto:${aisData.spravce.email}`}>
									{aisData.spravce.email}
								</a>
							</td>
						</tr>
					)}
				</tbody>
			</DataTable>
		</div>
	);
};

AisView.displayName = 'AisView';
